import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { isEnterWithoutComposing } from '@/common/util/keyboard'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import LinesTextarea from '@/components/view/atoms/textarea/LinesTextarea'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { useAuth } from '../../AuthContext'
import ModalOverlay from './ModalOverlay'

// 共通スタイル（SmartHub デザイントークン）
const CARD = 'rounded-xl border border-secondary bg-surface shadow-area'
const INPUT = 'h-8 rounded-lg border border-border-input bg-surface px-2.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TEXTAREA = 'w-full resize-y rounded-lg border border-border-input bg-surface px-2.5 py-1.5 text-size-13 text-primary outline-none hover:border-border-input-hover focus:border-border-input-active placeholder:text-placeholder'
const TH = 'text-secondary border-b border-secondary px-2.5 py-1.5 text-left text-[11px] font-semibold whitespace-nowrap'
const TD = 'text-primary px-2.5 py-1.5 align-middle'
const LABEL = 'text-secondary text-[11px] font-medium'
const FIELD = 'flex flex-col gap-1'

// 配信設定（仕様書 3.1/3.2/3.3 統合 / メニュー①②③）
export default function AIPurchaseAssistCampaigns() {
  const { siteId } = useParams()
  const { authHeaders } = useAuth()

  const [campaigns, setCampaigns] = useState([])
  const [counts, setCounts] = useState({ draft: 0, published: 0, paused: 0 })
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [targetMode, setTargetMode] = useState('')
  const [search, setSearch] = useState('')
  const [period, setPeriod] = useState(30)
  const [page, setPage] = useState(1)
  const [perPage] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [aiProfiles, setAiProfiles] = useState([])
  const [selectedId, setSelectedId] = useState(null)
  const [showCreate, setShowCreate] = useState(false)

  const fetchSummary = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns-summary?days=${period}`, { headers: authHeaders() })
      if (r.ok) setSummary(await r.json())
    } catch (e) {/* ignore */}
  }

  const fetchAll = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(perPage) })
      if (statusFilter) params.set('status', statusFilter)
      if (targetMode) params.set('target_mode', targetMode)
      if (search.trim()) params.set('search', search.trim())
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns-list?${params}`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setCampaigns(d.items || [])
      setTotalPages(d.total_pages || 1)
      // ステータス別カウント
      const r2 = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns`, { headers: authHeaders() })
      if (r2.ok) {
        const d2 = await r2.json()
        setCounts(d2.counts_by_status || counts)
      }
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  const fetchProfiles = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/ai-profiles`, { headers: authHeaders() })
      if (r.ok) {
        const d = await r.json()
        setAiProfiles(d.items || [])
      }
    } catch (e) {/* ignore */}
  }

  useEffect(() => { fetchAll() }, [siteId, statusFilter, targetMode, page])
  useEffect(() => { fetchProfiles(); fetchSummary() }, [siteId, period])

  const STATUS_BADGE = {
    draft: { label: '📝 下書き', cls: 'bg-surface-warning text-warning' },
    published: { label: '✅ 公開中', cls: 'bg-surface-success text-success' },
    paused: { label: '⏸ 停止中', cls: 'bg-surface-secondary text-secondary' },
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='📡 配信設定'
        description='Findeeウィジェットの配信設定。対象ページ・起動条件・デザイン・AI動作を一括管理。'
        rightNode={
          <div className='flex flex-wrap items-center gap-1.5'>
            <Button color={ButtonColor.PRIMARY} onClick={() => setShowCreate(true)}>+ 新規配信作成</Button>
            <Button color={ButtonColor.SECONDARY} onClick={fetchAll}>🔄 更新</Button>
          </div>
        }
      />

      {error && (
        <div className='bg-surface-critical text-critical mb-3 rounded-lg p-2.5 text-xs'>
          取得エラー: {error}
        </div>
      )}

      {/* KPIサマリーカード4枚（画面01仕様） */}
      {summary && (
        <div className='mb-3.5 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-2.5'>
          <SummaryKpiCard label="📡 公開中の配信" value={summary.campaign_count} />
          <SummaryKpiCard
            label={`🚀 エージェント起動回数 (${summary.period_days}日)`}
            value={summary.trigger_count.toLocaleString()}
            delta={summary.trigger_delta_pct}
            deltaUnit="%"
          />
          <SummaryKpiCard
            label="💰 経由CVR"
            value={`${summary.cvr.toFixed(2)}%`}
            delta={summary.cvr_delta_pt}
            deltaUnit="pt"
          />
          <SummaryKpiCard
            label="💸 経由GMV"
            value={`¥${summary.gmv.toLocaleString()}`}
            delta={summary.gmv_delta_pct}
            deltaUnit="%"
          />
        </div>
      )}

      {/* 検索・フィルター（画面01仕様） */}
      <div className={`${CARD} mb-3 flex flex-wrap items-center gap-1.5 p-2.5`}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={e => { if (isEnterWithoutComposing(e)) { setPage(1); fetchAll() } }}
          placeholder="🔍 配信名で検索..."
          className={`${INPUT} min-w-[200px] flex-1`}
        />
        <select value={targetMode} onChange={e => { setTargetMode(e.target.value); setPage(1) }} className={`${INPUT} w-[130px]`}>
          <option value="">対象すべて</option>
          <option value="all">全ページ</option>
          <option value="product_detail">商品詳細</option>
          <option value="category">カテゴリ</option>
          <option value="url">URL指定</option>
        </select>
        <select value={period} onChange={e => setPeriod(parseInt(e.target.value))} className={`${INPUT} w-[100px]`}>
          <option value={7}>過去7日</option>
          <option value={30}>過去30日</option>
          <option value={90}>過去90日</option>
        </select>
        <Button color={ButtonColor.PRIMARY} onClick={() => { setPage(1); fetchAll() }}>検索</Button>
        <Button color={ButtonColor.SECONDARY} onClick={() => { setSearch(''); setStatusFilter(''); setTargetMode(''); setPeriod(30); setPage(1) }}>リセット</Button>
      </div>

      {/* ヘッダー（ステータスフィルター） */}
      <div className='mb-3.5 flex flex-wrap items-center gap-1.5'>
        <span className='text-secondary mr-1 text-[11px]'>フィルター:</span>
        {[
          { key: '', label: 'すべて', count: campaigns.length },
          { key: 'draft', label: '📝 下書き', count: counts.draft },
          { key: 'published', label: '✅ 公開中', count: counts.published },
          { key: 'paused', label: '⏸ 停止中', count: counts.paused },
        ].map(f => {
          const on = statusFilter === f.key
          return (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              className={`cursor-pointer rounded-lg px-4 py-2 text-xs font-semibold ${on ? 'bg-fill-brand text-on-bg-fill shadow-button-primary' : 'bg-surface-secondary text-secondary hover:bg-surface-hover'}`}
            >{f.label} ({f.count})</button>
          )
        })}
      </div>

      {/* 一覧 */}
      {loading ? (
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      ) : campaigns.length === 0 ? (
        <div className={`${CARD} text-secondary p-8 text-center text-xs`}>
          配信設定がありません。「+ 新規配信作成」から開始してください。
        </div>
      ) : (
        <div className={`${CARD} overflow-x-auto`}>
          <table className='w-full border-collapse text-xs'>
            <thead>
              <tr className='bg-surface-secondary'>
                <th className={TH}>ID</th>
                <th className={TH}>配信名</th>
                <th className={TH}>対象</th>
                <th className={TH}>ステータス</th>
                <th className={TH}>AIプロファイル</th>
                <th className={`${TH} text-right`}>起動数(7日)</th>
                <th className={`${TH} text-right`}>CVR(7日)</th>
                <th className={TH}>更新日</th>
                <th className={TH}>操作</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map(c => {
                const sb = STATUS_BADGE[c.status] || STATUS_BADGE.draft
                const profile = aiProfiles.find(p => p.id === c.ai_profile_id)
                const TARGET_BADGE = {
                  all: { label: '全P', cls: 'bg-surface-emphasis text-emphasis' },
                  product_detail: { label: 'PD', cls: 'bg-surface-emphasis text-emphasis' },
                  category: { label: 'カテ', cls: 'bg-surface-success text-success' },
                  url: { label: 'URL', cls: 'bg-surface-warning text-warning' },
                }
                const tb = TARGET_BADGE[c.target_mode] || TARGET_BADGE.all
                return (
                  <tr key={c.id} className='border-b border-secondary'>
                    <td className={`${TD} text-secondary font-mono`}>#{c.id}</td>
                    <td className={`${TD} font-semibold`}>{c.name}</td>
                    <td className={TD}>
                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${tb.cls}`}>{tb.label}</span>
                    </td>
                    <td className={TD}>
                      <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${sb.cls}`}>
                        {sb.label}
                      </span>
                    </td>
                    <td className={`${TD} text-secondary text-[11px]`}>
                      {profile ? `${profile.name} (v${profile.version_number})` : <span className='text-critical'>未設定</span>}
                    </td>
                    <td className={`${TD} text-right font-mono`}>{(c.trigger_count_7d || 0).toLocaleString()}</td>
                    <td className={`${TD} text-right ${c.cvr_7d > 0 ? 'text-success' : 'text-secondary'}`}>
                      {(c.trigger_count_7d || 0) > 0 ? `${c.cvr_7d.toFixed(2)}%` : '-'}
                    </td>
                    <td className={`${TD} text-secondary text-[10px]`}>
                      {c.updated_at ? new Date(c.updated_at).toLocaleString('ja-JP') : '-'}
                    </td>
                    <td className={TD}>
                      <Button color={ButtonColor.SECONDARY} onClick={() => setSelectedId(c.id)}>📝 編集</Button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {/* ページネーション */}
          <div className='text-secondary flex items-center justify-center gap-2 border-t border-secondary p-3 text-xs'>
            <Button color={ButtonColor.SECONDARY} onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}>前へ</Button>
            <span className='text-secondary'>{page} / {totalPages}</span>
            <Button color={ButtonColor.SECONDARY} onClick={() => setPage(page + 1)} disabled={page >= totalPages}>次へ</Button>
          </div>
        </div>
      )}

      {/* 編集オーバーレイ */}
      {selectedId && (
        <CampaignEditor
          siteId={siteId}
          campaignId={selectedId}
          authHeaders={authHeaders}
          aiProfiles={aiProfiles}
          onClose={() => setSelectedId(null)}
          onChanged={fetchAll}
        />
      )}

      {/* 新規作成オーバーレイ */}
      {showCreate && (
        <CreateCampaignOverlay
          siteId={siteId}
          authHeaders={authHeaders}
          aiProfiles={aiProfiles}
          onClose={() => setShowCreate(false)}
          onCreated={(c) => { setShowCreate(false); fetchAll(); setSelectedId(c.id) }}
        />
      )}
    </Main>
  )
}


// =====================
// KPIサマリーカード（画面01仕様）
// =====================
function SummaryKpiCard({ label, value, delta, deltaUnit }) {
  const deltaColor = delta === null || delta === undefined ? 'text-secondary' : (delta > 0 ? 'text-success' : delta < 0 ? 'text-critical' : 'text-secondary')
  return (
    <div className={`${CARD} p-3`} style={{ borderLeft: '3px solid var(--color-fill-brand)' }}>
      <div className='text-secondary mb-0.5 text-[10px]'>{label}</div>
      <div className='text-primary text-lg font-bold'>{value}</div>
      {delta !== null && delta !== undefined && (
        <div className={`mt-1 text-[10px] ${deltaColor}`}>
          前期間比: {delta > 0 ? '+' : ''}{delta}{deltaUnit}
        </div>
      )}
    </div>
  )
}


// =====================
// 編集オーバーレイ（ウィザード/タブ統合）
// =====================
function CampaignEditor({ siteId, campaignId, authHeaders, aiProfiles, onClose, onChanged }) {
  const [campaign, setCampaign] = useState(null)
  const [draft, setDraft] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('basic')
  const [saving, setSaving] = useState(false)
  const [embed, setEmbed] = useState(null)
  // サイト管理＞機能設定の Reco-Media ON/OFF。未契約なら併用を選ばせない
  const [enableRecoMedia, setEnableRecoMedia] = useState(true)

  const loadSite = async () => {
    try {
      const r = await fetch(`/api/sites/${siteId}`, { headers: authHeaders() })
      if (!r.ok) return
      const d = await r.json()
      setEnableRecoMedia(!!d.enable_reco_media)
    } catch {
      // 取れなくても配信設定は編集できる。既定は true のままにして、
      // 実際に使うかどうかはサーバ側の判定（widget/dialog）に任せる
    }
  }

  const load = async () => {
    setLoading(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${campaignId}`, { headers: authHeaders() })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      const d = await r.json()
      setCampaign(d)
      setDraft(d)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [campaignId])
  useEffect(() => { loadSite() }, [siteId])

  const isDirty = draft && campaign && JSON.stringify(draft) !== JSON.stringify(campaign)

  const save = async () => {
    setSaving(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${campaignId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          name: draft.name,
          description: draft.description,
          target_pages: draft.target_pages,
          target_users: draft.target_users,
          trigger_config: draft.trigger_config,
          design_config: draft.design_config,
          ai_profile_id: draft.ai_profile_id || 0,
          // recommend_rule_id は送らない。参照する実装が無く画面からも外したため。
          // 列は既存データ保持のため残してあり、未送信なら値はそのまま
          recommend_config: draft.recommend_config,
          priority: draft.priority,
        }),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || r.status)
      }
      const d = await r.json()
      setCampaign(d)
      setDraft(d)
      onChanged()
    } catch (e) {
      alert(`保存失敗: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  const publish = async () => {
    if (!window.confirm('この配信を公開します。よろしいですか？')) return
    if (isDirty) await save()
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${campaignId}/publish`, {
      method: 'POST', headers: authHeaders(),
    })
    if (r.ok) { await load(); onChanged() }
    else { const d = await r.json().catch(() => ({})); alert(`公開失敗: ${d.detail || r.status}`) }
  }

  const pause = async () => {
    if (!window.confirm('配信を停止します。よろしいですか？')) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${campaignId}/pause`, {
      method: 'POST', headers: authHeaders(),
    })
    if (r.ok) { await load(); onChanged() }
    else alert('停止失敗')
  }

  const remove = async () => {
    if (!window.confirm('この配信を削除します。紐づくウィジェットキーも削除されます。よろしいですか？')) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${campaignId}`, {
      method: 'DELETE', headers: authHeaders(),
    })
    if (r.ok) { onChanged(); onClose() }
    else alert('削除失敗')
  }

  const loadEmbed = async () => {
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${campaignId}/embed-script`, { headers: authHeaders() })
    if (r.ok) setEmbed(await r.json())
  }

  useEffect(() => { if (tab === 'widget' && !embed) loadEmbed() }, [tab])

  if (loading || !draft) {
    return (
      <Overlay onClose={onClose}>
        <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
      </Overlay>
    )
  }

  const TABS = [
    { key: 'basic', label: '① 基本・対象' },
    { key: 'trigger', label: '② 起動条件' },
    { key: 'design', label: '③ デザイン' },
    { key: 'ai', label: '④ AI動作' },
    { key: 'widget', label: '⑤ ウィジェット' },
    { key: 'test', label: '💬 対話テスト' },
  ]

  return (
    <Overlay onClose={onClose} maxWidth={920}>
      {/* ヘッダー。タブによっては中身が縦に長くなり、スクロールすると配信名も
          保存ボタンもタブも画面から消えてしまうため、上端に固定する */}
      <div className='bg-surface sticky top-0 z-10 border-b border-secondary p-4'>
        {/* 配信名と説明は同じ列に積んで左端を揃える。
            入力欄が広がりすぎないよう上限幅を置く（モーダル幅は920px） */}
        <div className='flex flex-wrap items-start gap-2.5'>
          <span className='text-tertiary pt-2 font-mono text-[11px]'>#{campaign.id}</span>
          <div className='flex min-w-[240px] max-w-[460px] flex-1 flex-col gap-1.5'>
            <input value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })}
              className={`${INPUT} h-auto py-1 text-base font-bold`}
            />
            <textarea value={draft.description || ''} onChange={e => setDraft({ ...draft, description: e.target.value })}
              placeholder="説明（任意）" rows={1}
              className={`${TEXTAREA} text-[11px]`}
            />
          </div>
          {campaign.status === 'published' && <span className='bg-surface-success text-success mt-1.5 rounded px-2 py-0.5 text-[10px] font-bold'>✅ 公開中</span>}
          {campaign.status === 'draft' && <span className='bg-surface-warning text-warning mt-1.5 rounded px-2 py-0.5 text-[10px] font-bold'>📝 下書き</span>}
          {campaign.status === 'paused' && <span className='bg-surface-secondary text-secondary mt-1.5 rounded px-2 py-0.5 text-[10px] font-bold'>⏸ 停止</span>}
        </div>
        <div className='mt-2.5 flex flex-wrap justify-between gap-1.5'>
          <div className='flex gap-1.5'>
            {campaign.status !== 'published' && <Button color={ButtonColor.PRIMARY} onClick={publish}>🚀 公開</Button>}
            {campaign.status === 'published' && <Button color={ButtonColor.SECONDARY} onClick={pause}>⏸ 停止</Button>}
            {campaign.status === 'paused' && <Button color={ButtonColor.PRIMARY} onClick={publish}>▶ 再開</Button>}
            <Button color={ButtonColor.CRITICAL} onClick={remove}>🗑 削除</Button>
          </div>
          <div className='flex gap-1.5'>
            {isDirty && <Button color={ButtonColor.SECONDARY} onClick={() => setDraft(campaign)}>取消</Button>}
            <Button color={ButtonColor.PRIMARY} onClick={save} disabled={!isDirty || saving}>{saving ? '保存中...' : '✓ 保存'}</Button>
            <Button color={ButtonColor.SECONDARY} onClick={onClose}>✕ 閉じる</Button>
          </div>
        </div>
      </div>

      {/* タブ */}
      <div className='flex flex-wrap gap-1.5 border-b border-secondary px-4 py-2.5'>
        {TABS.map(t => {
          const on = tab === t.key
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`cursor-pointer rounded-lg px-4 py-2 text-xs font-semibold ${on ? 'bg-fill-brand text-on-bg-fill shadow-button-primary' : 'bg-surface-secondary text-secondary hover:bg-surface-hover'}`}
            >{t.label}</button>
          )
        })}
      </div>

      <div className='p-4'>
        {tab === 'basic' && <BasicTab draft={draft} setDraft={setDraft} />}
        {tab === 'trigger' && <TriggerTab draft={draft} setDraft={setDraft} />}
        {tab === 'design' && <DesignTab draft={draft} setDraft={setDraft} aiProfiles={aiProfiles} />}
        {tab === 'ai' && (
          <AiTab
            draft={draft}
            setDraft={setDraft}
            aiProfiles={aiProfiles}
            enableRecoMedia={enableRecoMedia}
          />
        )}
        {tab === 'widget' && <WidgetTab embed={embed} siteId={siteId} campaignId={campaignId} authHeaders={authHeaders} onReload={loadEmbed} />}
        {tab === 'test' && <TestDialogTab siteId={siteId} campaignId={campaignId} campaign={campaign} authHeaders={authHeaders} />}
      </div>
    </Overlay>
  )
}


// =====================
// タブ: ① 基本・対象
// =====================
function BasicTab({ draft, setDraft }) {
  const tp = draft.target_pages || { mode: 'all', values: [] }
  const tu = draft.target_users || { mode: 'all', segment_id: null }
  return (
    <div className='flex flex-col gap-3.5'>
      <Field label="優先度" hint="配信はAPIキーと1対1で決まるため競合が起きず、現在この値は配信に影響しません（一覧の並び順にのみ使われます）">
        <input type="number" min={0} max={999} value={draft.priority || 100} onChange={e => setDraft({ ...draft, priority: parseInt(e.target.value) || 100 })} className={`${INPUT} w-[120px]`} />
      </Field>
      <SectionTitle>📍 配信対象ページ</SectionTitle>
      <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
        対象ページに合致しないページでは<strong>ウィジェットが表示されません</strong>。
        「商品詳細ページのみ」「指定カテゴリ」を使う場合は、設置タグから
        <code>data-product-id</code> / <code>data-category-id</code> を渡す必要があります。
        渡っていない場合は判定できないため<strong>表示する側に倒します</strong>（意図せず消えるのを防ぐため）。
      </div>
      <Field label="モード">
        <select value={tp.mode} onChange={e => setDraft({ ...draft, target_pages: { ...tp, mode: e.target.value } })} className={INPUT}>
          <option value="all">全ページ</option>
          <option value="product_detail">商品詳細ページのみ</option>
          <option value="category">指定カテゴリ</option>
          <option value="url">指定URL（複数可）</option>
        </select>
      </Field>
      {tp.mode === 'url' && (
        <Field label="照合方式">
          <select
            value={tp.match_type || 'prefix'}
            onChange={e => setDraft({ ...draft, target_pages: { ...tp, match_type: e.target.value } })}
            className={INPUT}
          >
            <option value="prefix">前方一致（既定）</option>
            <option value="exact">完全一致</option>
            <option value="regex">正規表現</option>
          </select>
        </Field>
      )}
      {(tp.mode === 'category' || tp.mode === 'url') && (
        <Field label={tp.mode === 'category' ? 'カテゴリ名（1行に1つ）' : 'URLパターン（1行に1つ）'}>
          <LinesTextarea
            rows={4}
            lines={tp.values || []}
            onChange={v => setDraft({ ...draft, target_pages: { ...tp, values: v } })}
            placeholder={tp.mode === 'url' ? '例: /products/\n例: https://example.com/campaign/summer' : '例: アウトドア、ファッション'}
            className={TEXTAREA}
          />
        </Field>
      )}
      {tp.mode === 'url' && (
        <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
          <code>/products/</code> のような<strong>パス</strong>でも、<code>https://example.com/products/</code> のような
          <strong>絶対URL</strong>でも書けます。どちらかに一致すれば対象です。
          <br />
          ワイルドカード（<code>*</code>）は使えません。「<code>/products/</code> 配下すべて」は
          <strong>前方一致</strong>で <code>/products/</code> と書きます。
          細かい条件は<strong>正規表現</strong>を選んでください（例: <code>^/products/\d+$</code>）。
        </div>
      )}
      <SectionTitle>👥 配信対象ユーザー</SectionTitle>
      <Field label="モード">
        <select value={tu.mode} onChange={e => setDraft({ ...draft, target_users: { ...tu, mode: e.target.value } })} className={INPUT}>
          <option value="all">全員</option>
          <option value="members_only">会員のみ</option>
          <option value="non_members">非会員のみ</option>
          <option value="segment">セグメント指定（未対応）</option>
        </select>
      </Field>
      {(tu.mode === 'members_only' || tu.mode === 'non_members') && (
        <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
          判定には設置タグからログイン状態を渡す必要があります。渡っていない場合は
          <strong>制限せず全員に表示</strong>します（黙って表示されなくなるのを避けるため）。
        </div>
      )}
      {tu.mode === 'segment' && (
        <>
          <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
            セグメント指定は<strong>未対応</strong>です。選択しても配信は制限されません。
          </div>
          <Field label="セグメントID" hint="Hub既存セグメントのID（後で連携実装）">
            <input value={tu.segment_id || ''} onChange={e => setDraft({ ...draft, target_users: { ...tu, segment_id: e.target.value } })} placeholder="seg_xxx" className={INPUT} />
          </Field>
        </>
      )}
    </div>
  )
}


// =====================
// タブ: ② 起動条件
// =====================
function TriggerTab({ draft, setDraft }) {
  const t = draft.trigger_config || {}
  const update = (k, v) => setDraft({ ...draft, trigger_config: { ...t, [k]: v } })
  const updateDevice = (k, v) => setDraft({ ...draft, trigger_config: { ...t, devices: { ...(t.devices || {}), [k]: v } } })
  const updateHours = (k, v) => setDraft({ ...draft, trigger_config: { ...t, hours: { ...(t.hours || {}), [k]: v } } })
  return (
    <div className='flex flex-col gap-3.5'>
      <SectionTitle>🚦 起動方式</SectionTitle>
      <CheckRow
        label="画面隅に呼び出しUIを出す（既定ON）"
        checked={t.floating !== false} onChange={v => update('floating', v)}
      />
      <p className='text-tertiary -mt-2 pl-5 text-[10px]'>
        見た目（タブ／円形アイコン・ラベル・位置）は「③ デザイン」で設定します。
        設置ページに <code>data-findee-slot</code> のマウントポイントがある場合はそちらが優先され、これは出ません。
      </p>
      <CheckRow label="ページを開いたときに会話を自動で開く" checked={!!t.auto_expand} onChange={v => update('auto_expand', v)} />
      {t.auto_expand && (
        <Field label="開くまでの遅延（秒）" hint="0＝すぐ開く。自動で開くときだけ使います">
          <input type="number" min={0} max={60} value={t.delay_sec || 0} onChange={e => update('delay_sec', parseInt(e.target.value) || 0)} className={`${INPUT} w-[120px]`} />
        </Field>
      )}

      <SectionTitle>📱 表示するデバイス</SectionTitle>
      <p className='text-tertiary -mt-2 text-[10px]'>チェックを外したデバイスでは表示されません（既定はすべて表示）。</p>
      <div className='flex flex-wrap gap-3'>
        <CheckRow label="PC" checked={t.devices?.pc !== false} onChange={v => updateDevice('pc', v)} />
        <CheckRow label="スマホ" checked={t.devices?.sp !== false} onChange={v => updateDevice('sp', v)} />
        <CheckRow label="タブレット" checked={t.devices?.tablet !== false} onChange={v => updateDevice('tablet', v)} />
      </div>

      <SectionTitle>🕒 表示する時間帯</SectionTitle>
      <CheckRow label="時間帯を指定する（既定は24時間表示）" checked={!!t.hours?.enabled} onChange={v => updateHours('enabled', v)} />
      {t.hours?.enabled && (
        <>
          <div className='flex items-center gap-2'>
            <input type="time" value={t.hours.start || '00:00'} onChange={e => updateHours('start', e.target.value)} className={`${INPUT} w-[120px]`} />
            <span className='text-secondary'>〜</span>
            <input type="time" value={t.hours.end || '23:59'} onChange={e => updateHours('end', e.target.value)} className={`${INPUT} w-[120px]`} />
          </div>
          <p className='text-secondary text-[11px]'>
            日本時間で判定します。<strong>22:00〜02:00</strong> のように日をまたぐ指定もできます。時間帯の外ではウィジェットが表示されません。
          </p>
        </>
      )}
    </div>
  )
}


// =====================
// タブ: ③ デザイン
// =====================
// 表示形態の既定値（バックエンド schemas/purchase_assist_widget.py と一致させること）
const PANEL_DEFAULTS = { form: 'auto', side: 'right', width_pc: 380, sp_height: '70dvh', push_content: false, always_open: false, z_index: 99998 }
const HANDLE_DEFAULTS = { shape: 'tab', label: 'AIに相談', icon: '' }
const SLOT_DEFAULTS = { expand_mode: 'handoff', open_on: 'submit', placeholder: 'メッセージを入力...', inline_height: 520 }

function DesignTab({ draft, setDraft, aiProfiles = [] }) {
  const d = draft.design_config || {}
  const update = (k, v) => setDraft({ ...draft, design_config: { ...d, [k]: v } })

  // 開始メッセージ・質問例は AIプロファイルが持つ。プレビューは選択中プロファイルの値を映す
  const selectedProfile = aiProfiles.find(p => p.id === draft.ai_profile_id)
  const profileResponse = selectedProfile?.response_config || {}
  const openingMessage = profileResponse.initial_message || 'こんにちは！何かお探しでしょうか？'
  const sq = Array.isArray(profileResponse.suggested_questions) ? profileResponse.suggested_questions : []

  const panel = { ...PANEL_DEFAULTS, ...(d.panel_config || {}) }
  const handle = { ...HANDLE_DEFAULTS, ...(d.handle_config || {}) }
  const slots = { ...SLOT_DEFAULTS, ...(d.slot_defaults || {}) }
  const updatePanel = (k, v) => update('panel_config', { ...panel, [k]: v })
  const updateHandle = (k, v) => update('handle_config', { ...handle, [k]: v })
  const updateSlots = (k, v) => update('slot_defaults', { ...slots, [k]: v })

  const [preview, setPreview] = useState('handle')
  // 初期設定で決めたら普段は触らない項目は畳んでおく
  const [showAdvanced, setShowAdvanced] = useState(false)

  return (
    <div className='flex flex-col gap-3.5'>
      {/* プレビューを先頭に置く。設定を触るたびに結果が見えるようにする */}
      <div className='flex flex-wrap items-center justify-between gap-1.5'>
        <div className='flex gap-1.5'>
          {[['handle', '呼び出しUI'], ['sidebar', 'パネル'], ['inline', 'インライン枠']].map(([k, label]) => (
            <button
              key={k} type='button' onClick={() => setPreview(k)}
              className={`cursor-pointer rounded-full border px-3 py-1 text-[11px] font-bold ${
                preview === k
                  ? 'bg-surface-emphasis text-emphasis border-emphasis'
                  : 'bg-surface-secondary text-secondary border-secondary hover:bg-surface-hover'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <span className='text-tertiary text-[10px]'>
          色は「リッチUIデザイナー ＞ 共通テーマ ＞ アクセント色」で設定します
        </span>
      </div>
      <WidgetPreview
        mode={preview} design={d} theme={draft.rich_ui_theme || {}}
        panel={panel} handle={handle} slots={slots}
        openingMessage={openingMessage} suggested={sq}
      />

      <div className='grid grid-cols-2 gap-3.5'>
        <Field label="呼び出しUIの形態" hint="円形アイコンはAIスタッフと混同されやすいため、既定はラベル付きタブ">
          <select value={handle.shape} onChange={e => updateHandle('shape', e.target.value)} className={INPUT}>
            <option value="tab">ラベル付きタブ（既定）</option>
            <option value="fab">円形アイコン</option>
          </select>
        </Field>
        {/* 2026-10-03 修正：表示位置は円形アイコンだけに効く（SMART_HUB_DEV-117）。
            ラベル付きタブはウィジェットJS（makeTabHandle）でパネルを出す辺の画面端・縦中央に置くため、
            タブのときに選ばせると、選んだ位置と表示が食い違って見えていた */}
        {handle.shape === 'fab' ? (
          <Field label="表示位置">
            <select value={d.position || 'br'} onChange={e => update('position', e.target.value)} className={INPUT}>
              <option value="br">右下</option>
              <option value="bl">左下</option>
              <option value="center">中央下</option>
            </select>
          </Field>
        ) : (
          <Field label="表示位置">
            <div className='text-secondary py-2 text-xs'>
              画面の{panel.side === 'left' ? '左' : '右'}端・縦中央に表示します（下の「表示する辺」で切り替え）
            </div>
          </Field>
        )}
        <Field label="呼び出しUIのラベル">
          <input value={handle.label} onChange={e => updateHandle('label', e.target.value)} placeholder="AIに相談" className={INPUT} />
        </Field>
        <Field label="呼び出しUIのアイコン" hint="絵文字1文字。空ならタブ=🔍 / 円形=💬">
          <input value={handle.icon} onChange={e => updateHandle('icon', e.target.value)} placeholder="🔍" className={INPUT} />
        </Field>
      </div>
      <Field label="パネルの形態" hint="会話を開いたときの見え方">
        <select value={panel.form} onChange={e => updatePanel('form', e.target.value)} className={INPUT}>
          <option value="auto">自動（PC=サイドパネル / SP=ボトムシート）</option>
          <option value="sidebar">常にサイドパネル</option>
          <option value="bottom_sheet">常にボトムシート</option>
          <option value="popover">ポップオーバー（従来型）</option>
        </select>
      </Field>

      {/* ここから下は初期設定で決めたら普段は触らない項目。既定のままで運用できる */}
      <button
        type='button' onClick={() => setShowAdvanced(v => !v)}
        className='text-secondary hover:bg-surface-hover flex w-fit cursor-pointer items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold'
      >
        {showAdvanced ? '▼' : '▶'} 詳細設定（サイズ・重なり順・ページ内設置）
      </button>
      {showAdvanced && (
      <div className='flex flex-col gap-3.5 border-l-2 border-secondary pl-3'>
      <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
        設置ページに <code>data-findee-slot</code> / <code>data-findee-open</code> のマウントポイントが
        1つでもあれば、そちらが優先されます。上の「呼び出しUI」は
        <strong>マウントポイントが無いページ</strong>で使われます。
      </div>
      <div className='grid grid-cols-2 gap-3.5'>
        <Field label="テキストカラー" hint="起動ボタンの文字色。背景色はリッチUIのアクセント色">
          <input type="color" value={d.text_color || '#ffffff'} onChange={e => update('text_color', e.target.value)} className='h-9 w-full cursor-pointer rounded-lg border border-border-input bg-surface p-0.5' />
        </Field>
        <Field label="パネルのヘッダー" hint="ヘッダーのタイトル先頭に 🛒 を付ける">
          <CheckRow label="🛒 を表示する" checked={!!d.logo} onChange={v => update('logo', v)} />
        </Field>
        <Field label="表示する辺">
          <select value={panel.side} onChange={e => updatePanel('side', e.target.value)} className={INPUT}>
            <option value="right">右</option>
            <option value="left">左</option>
          </select>
        </Field>
        <Field label="パネル幅（PC・px）">
          <input type="number" min={280} max={720} value={panel.width_pc}
                 onChange={e => updatePanel('width_pc', parseInt(e.target.value) || 380)} className={INPUT} />
        </Field>
        <Field label="パネル高さ（SP）" hint="ボトムシートの高さ。dvh 推奨（アドレスバー伸縮に追従）">
          <input value={panel.sp_height} onChange={e => updatePanel('sp_height', e.target.value)} placeholder="70dvh" className={INPUT} />
        </Field>
        <Field label="重なり順（z-index）" hint="顧客サイトのヘッダやモーダルとの前後関係を調整する">
          <input type="number" value={panel.z_index}
                 onChange={e => updatePanel('z_index', parseInt(e.target.value) || 99998)} className={INPUT} />
        </Field>
      </div>
      <CheckRow label="ページ表示時にパネルを開いておく" checked={!!panel.always_open} onChange={v => updatePanel('always_open', v)} />
      <CheckRow label="パネルを開いたときに本文を押し出す（既定OFF）" checked={!!panel.push_content} onChange={v => updatePanel('push_content', v)} />
      {panel.push_content && (
        <div className='bg-surface-critical text-critical rounded-lg p-2.5 text-[11px]'>
          ⚠ 本文の押し出しは<strong>顧客サイトのレイアウトに影響します</strong>。固定ヘッダや画面幅いっぱいの
          要素を持つサイトでは表示が崩れることがあります。設置サイトで必ず確認してください。
          既定のオーバーレイ（重ねて表示）ならレイアウトに影響しません。
        </div>
      )}

      {/* ---------------- ページ内設置の既定 ---------------- */}
      <SectionTitle>🧩 ページ内設置の既定</SectionTitle>
      <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
        マウントポイントごとに <code>data-findee-expand</code> などの属性で個別に上書きできます。
      </div>
      <div className='grid grid-cols-2 gap-3.5'>
        <Field label="入力欄の展開挙動" hint="handoff＝入力はその場・会話はパネル / inplace＝埋め込み枠内で完結">
          <select value={slots.expand_mode} onChange={e => updateSlots('expand_mode', e.target.value)} className={INPUT}>
            <option value="handoff">パネルへ引き継ぐ（既定）</option>
            <option value="inplace">埋め込み枠内で展開</option>
          </select>
        </Field>
        <Field label="開くきっかけ" hint="合焦で開くとスマホでキーボードと重なるため、既定は送信時">
          <select value={slots.open_on} onChange={e => updateSlots('open_on', e.target.value)} className={INPUT}>
            <option value="submit">送信したとき（既定）</option>
            <option value="focus">入力欄に触れたとき</option>
          </select>
        </Field>
        <Field label="入力欄のプレースホルダ">
          <input value={slots.placeholder} onChange={e => updateSlots('placeholder', e.target.value)} className={INPUT} />
        </Field>
        <Field label="インライン枠の高さ（px）">
          <input type="number" min={280} max={900} value={slots.inline_height}
                 onChange={e => updateSlots('inline_height', parseInt(e.target.value) || 520)} className={INPUT} />
        </Field>
      </div>
      {slots.expand_mode === 'inplace' && (
        <div className='bg-surface-warning text-warning rounded-lg p-2.5 text-[11px]'>
          ⚠ 枠内展開は「AIに相談」<strong>専用のセクションを用意したページ向け</strong>です。商品説明の途中などに
          置くと、回答（比較表・商品ギャラリー等）で縦に伸びて本文を押し下げます。
          幅が 320px 未満の枠では自動的にパネル引き継ぎに切り替わります。
        </div>
      )}

      </div>
      )}
      <div className='text-tertiary text-[10px]'>
        プレビューの文言は{selectedProfile ? `AIプロファイル「${selectedProfile.name}」` : 'AIプロファイル未選択のため既定値'}のものです。
      </div>
    </div>
  )
}

/** 配信設定のデザインを反映した簡易プレビュー。実際のウィジェットJSとは別実装のため、見た目の確認用。 */
function WidgetPreview({ mode, design, theme = {}, panel, handle, slots, openingMessage, suggested }) {
  // 色の決め方はウィジェットJS（services/rich_ui_widget_js.py の injectStyles）と揃える。
  // ここだけ design.main_color を見ていたため、プレビューだけ変わって実機が変わらなかった
  const accent = theme.accent_color || design.main_color || '#6366f1'
  const fg = design.text_color || '#ffffff'
  const isLeft = panel.side === 'left'
  const stage = { position: 'relative', height: 260, overflow: 'hidden', borderRadius: 8, background: '#f3f4f6' }
  const bubble = { padding: '8px 10px', background: '#f3f4f6', borderRadius: 10, fontSize: 11, color: '#111' }
  const chip = { padding: '4px 8px', borderRadius: 12, background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.1)', fontSize: 10, color: accent }

  const conversation = (
    <>
      <div style={bubble}>{openingMessage}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
        {suggested.slice(0, 3).map((q, i) => <span key={i} style={chip}>{q}</span>)}
      </div>
    </>
  )
  const inputRow = (
    <div style={{ display: 'flex', gap: 6, padding: 8, borderTop: '1px solid #eee' }}>
      <div style={{ flex: 1, padding: '6px 8px', border: '1px solid #ddd', borderRadius: 6, fontSize: 11, color: '#9ca3af', background: '#fff' }}>
        {slots.placeholder}
      </div>
      <div style={{ padding: '6px 12px', borderRadius: 6, background: accent, color: fg, fontSize: 11 }}>送信</div>
    </div>
  )

  if (mode === 'handle') {
    return (
      <div className='bg-surface-secondary rounded-lg p-4'>
        <div style={stage}>
          <div style={{ padding: 12, color: '#9ca3af', fontSize: 11 }}>顧客サイトのページ</div>
          {handle.shape === 'fab' ? (
            <div style={{
              position: 'absolute', bottom: 16,
              // 2026-10-03 修正：中央下が右下と同じ位置に描かれていた（SMART_HUB_DEV-118）。
              // ウィジェットJS（makeFabHandle）と同じく br / bl / center の3通りで置く
              // 修正前: [design.position === 'bl' ? 'left' : 'right']: 16,
              ...(design.position === 'bl' ? { left: 16 }
                : design.position === 'center' ? { left: '50%', transform: 'translateX(-50%)' }
                : { right: 16 }),
              width: 48, height: 48, borderRadius: '50%', background: accent, color: fg,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
              boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            }}>{handle.icon || '💬'}</div>
          ) : (
            <div style={{
              position: 'absolute', top: '50%', transform: 'translateY(-50%)',
              [isLeft ? 'left' : 'right']: 0,
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              padding: '12px 7px', background: accent, color: fg,
              borderRadius: isLeft ? '0 10px 10px 0' : '10px 0 0 10px',
              fontSize: 11, fontWeight: 700, boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            }}>
              <span style={{ fontSize: 14 }}>{handle.icon || '🔍'}</span>
              <span style={{ writingMode: 'vertical-rl', textOrientation: 'upright' }}>{handle.label}</span>
            </div>
          )}
        </div>
      </div>
    )
  }

  if (mode === 'sidebar') {
    const width = Math.max(160, Math.min(panel.width_pc, 420)) * 0.7
    return (
      <div className='bg-surface-secondary rounded-lg p-4'>
        <div style={stage}>
          <div style={{
            padding: 12, color: '#9ca3af', fontSize: 11,
            marginRight: !isLeft && panel.push_content ? width : 0,
            marginLeft: isLeft && panel.push_content ? width : 0,
          }}>
            顧客サイトのページ{panel.push_content ? '（押し出しON：本文が退避）' : '（押し出しOFF：重ねて表示）'}
          </div>
          <div style={{
            position: 'absolute', top: 0, bottom: 0, [isLeft ? 'left' : 'right']: 0, width,
            background: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column',
          }}>
            <div style={{ padding: '8px 10px', background: accent, color: fg, fontSize: 11, fontWeight: 700 }}>
              {design.logo ? '🛒 ' : ''}{handle.label}
            </div>
            <div style={{ flex: 1, padding: 10, overflow: 'hidden' }}>{conversation}</div>
            {inputRow}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='bg-surface-secondary rounded-lg p-4'>
      <div style={{ ...stage, background: '#fff', border: '1px solid #e5e7eb' }}>
        <div style={{ padding: 12, color: '#9ca3af', fontSize: 11 }}>商品説明などのページ本文</div>
        <div style={{
          margin: '0 12px', height: 170, border: '1px solid #e5e7eb', borderRadius: 8,
          display: 'flex', flexDirection: 'column', overflow: 'hidden', background: '#fff',
        }}>
          <div style={{ flex: 1, padding: 10, overflow: 'hidden' }}>{conversation}</div>
          {inputRow}
        </div>
      </div>
      <div className='text-tertiary mt-1.5 text-[10px]'>
        実際の高さは {slots.inline_height}px（インライン枠）。入力欄のみの設置では、
        {slots.expand_mode === 'inplace' ? '送信時にこの枠が生えます。' : '送信時にパネルへ引き継ぎます。'}
      </div>
    </div>
  )
}


// =====================
// タブ: ④ AI動作
// =====================
function AiTab({ draft, setDraft, aiProfiles, enableRecoMedia = true }) {
  // 推薦の候補の集め方。条件（在庫・販促・件数）はAIプロファイル側が持つ
  const rc = draft.recommend_config || {}
  const updateReco = (k, v) => setDraft({ ...draft, recommend_config: { ...rc, [k]: v } })

  return (
    <div className='flex flex-col gap-3.5'>
      <SectionTitle>🎭 AIプロファイル選択</SectionTitle>
      <Field label="プロファイル" hint="AIプロファイル管理画面で作成したプロファイルから選択。公開には必須。">
        <select value={draft.ai_profile_id || ''} onChange={e => setDraft({ ...draft, ai_profile_id: e.target.value ? parseInt(e.target.value) : null })} className={INPUT}>
          <option value="">— 未選択 —</option>
          {aiProfiles.map(p => (
            <option key={p.id} value={p.id}>{p.name} (v{p.version_number}) {p.is_active ? '' : '[無効]'}</option>
          ))}
        </select>
      </Field>
      {draft.ai_profile_id && aiProfiles.find(p => p.id === draft.ai_profile_id) && (
        <div className='bg-surface-emphasis text-emphasis rounded-lg p-2.5 text-[11px]'>
          選択中: <strong>{aiProfiles.find(p => p.id === draft.ai_profile_id).name}</strong>
          {aiProfiles.find(p => p.id === draft.ai_profile_id).description && (
            <div className='text-secondary mt-1'>
              {aiProfiles.find(p => p.id === draft.ai_profile_id).description}
            </div>
          )}
        </div>
      )}

      <SectionTitle>🛒 商品の推薦</SectionTitle>
      <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
        <strong>会話の内容から商品を探す動きは常に行います。</strong>
        ここで決めるのは、それに<strong>表示中の商品を起点にした候補を足すか</strong>です。
        <br />
        在庫切れの扱い・クーポンの案内可否・推薦件数などの<strong>条件は
        「AIプロファイル管理」の「🛒 推薦」タブ</strong>で設定します（どちらの集め方で
        集めた候補でも同じ条件で絞り込まれます）。
      </div>
      {/* 「会話の内容から探す」は常に動く。ここで選ぶのは**足すかどうか**なので、
          2択のプルダウンではなくチェックボックスにする。プルダウンだと
          「意味検索 か Reco-Media か」の二者択一に見え、実装と食い違う */}
      <Field
        label="Reco-Media のロジックを併用する"
        hint="会話の内容から探すのは常に行う。これをONにすると、表示中の商品を起点にした候補も足す"
      >
        <label className={`flex items-center gap-2 ${enableRecoMedia ? '' : 'opacity-50'}`}>
          <input
            type="checkbox"
            checked={rc.source === 'vector_plus_reco'}
            disabled={!enableRecoMedia}
            onChange={e => updateReco('source', e.target.checked ? 'vector_plus_reco' : 'vector')}
          />
          <span className='text-[13px]'>
            表示中の商品から候補を足す（併売・閲覧併売・類似商品）
          </span>
        </label>
        {!enableRecoMedia && (
          <div className='text-secondary mt-1 text-[11px]'>
            このサイトは Reco-Media が OFF のため選べません。
            サイト管理 ＞ 機能設定 ＞ Reco-Media を ON にすると利用できます。
          </div>
        )}
      </Field>
      {rc.source === 'vector_plus_reco' && enableRecoMedia && (
        <>
          <Field label="商品ページでのロジック" hint="表示中の商品を起点に候補を足す。設置タグから data-product-id を渡す必要あり">
            <select value={rc.product_logic || 'BB'} onChange={e => updateReco('product_logic', e.target.value)} className={INPUT}>
              <option value="BB">併売（この商品を買った人が買っている）</option>
              <option value="OB">閲覧併売（この商品を見た人が買っている）</option>
              <option value="similar">類似商品</option>
            </select>
          </Field>
          <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
            併売・閲覧併売は<strong>受注データが必要</strong>です。実績が溜まるまでは結果が出ないため、
            その間は自動的に<strong>類似商品</strong>で代替します（設定の変更は不要です）。
            商品IDが渡らないページでは意味検索だけで候補を作ります。
          </div>
        </>
      )}
      <Field label="候補が足りないとき" hint="意図の薄い質問では意味検索が候補を絞りきれないことがある">
        <select value={rc.fallback || 'none'} onChange={e => updateReco('fallback', e.target.value)} className={INPUT}>
          <option value="none">補わない（既定）</option>
          <option value="ranking">ランキングで補う</option>
        </select>
      </Field>
      <Field label="推薦候補の取得件数" hint="絞り込み前に集める件数。多いほど候補は残るが応答は遅くなる">
        <input
          type="number" min={1} max={20}
          value={rc.top_k || 5}
          onChange={e => updateReco('top_k', Math.max(1, Math.min(20, parseInt(e.target.value) || 5)))}
          className={`${INPUT} w-[120px]`}
        />
      </Field>
    </div>
  )
}


// =====================
// タブ: ⑤ ウィジェット詳細設定
// =====================
function WidgetTab({ embed, siteId, campaignId, authHeaders, onReload }) {
  const [domains, setDomains] = useState([])
  const [rateLimit, setRateLimit] = useState(60)
  const [keyName, setKeyName] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (embed) {
      setDomains(embed.allowed_domains || [])
      setRateLimit(embed.rate_limit_per_minute || 60)
    }
  }, [embed])

  if (!embed) {
    return <div className='text-secondary py-16 text-center text-sm'>読み込み中...</div>
  }

  const copyEmbed = () => {
    navigator.clipboard.writeText(embed.embed_html)
    alert('埋め込みコードをクリップボードにコピーしました')
  }

  const regenerate = async () => {
    if (!window.confirm('APIキーを再生成します。既存キーは無効化され、設置済みコードを更新する必要があります。よろしいですか？')) return
    const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${campaignId}/regenerate-key`, {
      method: 'POST', headers: authHeaders(),
    })
    if (r.ok) {
      alert('APIキーを再生成しました。新しい埋め込みコードを取得します。')
      onReload()
    } else {
      alert('再生成失敗')
    }
  }

  const saveSecurity = async () => {
    setSaving(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/widget-keys/${embed.key_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          allowed_domains: domains,
          rate_limit_per_minute: rateLimit,
        }),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      alert('セキュリティ設定を保存しました')
      onReload()
    } catch (e) {
      alert(`保存失敗: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className='flex flex-col gap-3.5'>
      <Section title="📦 埋め込みコード" note="ウィジェットを出すタグ" defaultOpen>
        <div className='bg-surface-brand text-on-bg-fill max-h-[200px] overflow-auto whitespace-pre-wrap rounded-lg p-3 font-mono text-xs'>
          {embed.embed_html}
        </div>
        <div className='flex gap-1.5'>
          <Button color={ButtonColor.SECONDARY} onClick={copyEmbed}>📋 コピー</Button>
          <Button color={ButtonColor.CRITICAL} onClick={regenerate}>🔁 APIキー再生成</Button>
        </div>
        <div className='bg-surface-emphasis text-secondary rounded-lg p-2 text-[10px]'>
          api_key: <code className='text-emphasis'>{embed.api_key}</code>
        </div>
      </Section>

      {/* 計測タグ（成果計測）。設置しないと経由CVR・GMVが取れない */}
      {embed.tracking_html && (
        <Section title="📈 計測タグ" note="成果計測に必須">
          <div className='bg-surface-warning text-secondary rounded-lg border border-secondary p-2.5 text-[11px] leading-relaxed'>
            {embed.tracking_note}
          </div>
          <div className='bg-surface-brand text-on-bg-fill max-h-[280px] overflow-auto whitespace-pre-wrap rounded-lg p-3 font-mono text-xs'>
            {embed.tracking_html}
          </div>
          <div className='flex gap-1.5'>
            <Button
              color={ButtonColor.SECONDARY}
              onClick={() => {
                navigator.clipboard.writeText(embed.tracking_html)
                alert('計測タグをクリップボードにコピーしました')
              }}
            >📋 コピー</Button>
          </div>
          <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px] leading-relaxed'>
            <div className='text-primary mb-1 font-bold'>設置位置</div>
            <ul className='m-0 list-disc pl-4'>
              <li>前半（setMerchantId + スクリプト読み込み）… <strong>全ページの <code>&lt;head&gt;</code></strong></li>
              <li>後半（purchase の送信）… <strong>注文完了ページのみ</strong>。注文が確定した後に実行してください</li>
            </ul>
            <div className='text-primary mt-2 mb-1 font-bold'>送信する値</div>
            <ul className='m-0 list-disc pl-4'>
              <li><code>order_id</code>（必須）… 注文番号。再送しても二重計上しない鍵になります</li>
              <li><code>total_price</code>… 注文合計金額（税・送料込み）。GMVに使います</li>
              <li><code>items</code>… 明細。推薦した商品が買われたかの判定に使います</li>
              <li><code>customer_id</code>（任意）… ログイン中の顧客ID。突合精度が上がります</li>
            </ul>
            <div className='text-tertiary mt-2'>
              このタグはウィジェットと独立して動きます。どちらか一方だけの設置でも動作します。
            </div>
          </div>
        </Section>
      )}

      {/* ページ内マウントポイント（任意設置） */}
      {Array.isArray(embed.mount_points) && embed.mount_points.length > 0 && (
        <Section title="🧩 ページ内に設置するマウントポイント" note="任意">
          {embed.mount_points_note && (
            <div className='text-secondary bg-surface-secondary rounded-lg p-2.5 text-[11px]'>
              {embed.mount_points_note}
            </div>
          )}
          {embed.mount_points.map(mp => (
            <div key={mp.key} className='rounded-lg border border-secondary p-2.5'>
              <div className='text-emphasis mb-1 text-xs font-bold'>{mp.title}</div>
              <div className='text-secondary mb-2 text-[11px]'>{mp.description}</div>
              <div className='bg-surface-secondary text-primary overflow-auto whitespace-pre-wrap rounded p-2 font-mono text-[11px]'>
                {mp.html}
              </div>
              <div className='mt-1.5'>
                <Button
                  color={ButtonColor.SECONDARY}
                  onClick={() => {
                    navigator.clipboard.writeText(mp.html)
                    alert(`「${mp.title}」のコードをコピーしました`)
                  }}
                >
                  📋 コピー
                </Button>
              </div>
            </div>
          ))}
        </Section>
      )}

      <Section title="🔒 セキュリティ設定" note="許可ドメイン・レート制限">
      <Field label="許可ドメイン（1行に1つ）" hint="このAPIキーで対話API呼び出しを許可するドメイン。空なら全許可（非推奨）。* でワイルドカード可。">
        <LinesTextarea
          rows={4}
          lines={domains}
          onChange={setDomains}
          placeholder="例:&#10;www.example.com&#10;*.example.com"
          className={`${TEXTAREA} font-mono text-[11px]`}
        />
      </Field>
      <Field label="Rate Limit（リクエスト/分）" hint="同一APIキーからの呼び出し上限">
        <input type="number" min={1} max={10000} value={rateLimit} onChange={e => setRateLimit(parseInt(e.target.value) || 60)} className={`${INPUT} w-[160px]`} />
      </Field>
      <div className='flex justify-end'>
        <Button color={ButtonColor.PRIMARY} onClick={saveSecurity} disabled={saving}>{saving ? '保存中...' : '✓ セキュリティ設定を保存'}</Button>
      </div>
      </Section>
    </div>
  )
}


// =====================
// 対話テストタブ
// =====================
function TestDialogTab({ siteId, campaignId, campaign, authHeaders }) {
  const [history, setHistory] = useState([])  // [{role, content, meta?, recommendations?}]
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [sessionToken, setSessionToken] = useState(null)
  const [stats, setStats] = useState({ totalCost: 0, totalTokens: 0, turnCount: 0 })

  const send = async () => {
    const text = input.trim()
    if (!text) return
    if (!campaign?.ai_profile_id) {
      setError('AIプロファイル未設定の配信ではテスト対話できません。④AI動作タブでプロファイルを選択してください。')
      return
    }
    setLoading(true)
    setError(null)
    const userMsg = { role: 'user', content: text }
    setHistory(h => [...h, userMsg])
    setInput('')
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns/${campaignId}/test-dialog`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          user_message: text,
          history: history.map(m => ({ role: m.role, content: m.content })),
          session_token: sessionToken,
        }),
      })
      // r.ok の判定より先に r.json() を呼ぶと、エラー応答がJSONでない場合
      // （502のHTML等）にパース失敗の例外だけが出て、本当の原因が画面に出なかった
      if (!r.ok) {
        const body = await r.text()
        let detail = body
        try {
          detail = JSON.parse(body).detail || body
        } catch {
          // JSONでない応答はそのまま原因として見せる
        }
        throw new Error(detail ? `HTTP ${r.status}: ${detail}` : `HTTP ${r.status}`)
      }
      const d = await r.json()
      setSessionToken(d.session_token)
      setHistory(h => [...h, {
        role: 'assistant',
        content: d.response,
        meta: d.meta,
        recommendations: d.recommendations || [],
        message_id: d.message_id,
        blocked: d.blocked,
      }])
      // 統計更新
      const costJpy = d.meta?.cost_jpy ?? 0
      const tokens = (d.meta?.input_tokens || 0) + (d.meta?.output_tokens || 0)
      setStats(s => ({
        totalCost: s.totalCost + (costJpy || 0),
        totalTokens: s.totalTokens + tokens,
        turnCount: s.turnCount + 1,
      }))
    } catch (e) {
      setError(e.message)
      setHistory(h => [...h, { role: 'system', content: `エラー: ${e.message}` }])
    } finally {
      setLoading(false)
    }
  }

  const reset = () => {
    if (history.length > 0 && !window.confirm('現在の会話履歴をクリアします。よろしいですか？')) return
    setHistory([])
    setSessionToken(null)
    setStats({ totalCost: 0, totalTokens: 0, turnCount: 0 })
    setError(null)
  }

  return (
    <div className='flex flex-col gap-3.5'>
      <div className='bg-surface-emphasis text-emphasis rounded-lg p-2.5 text-[11px]'>
        💡 この配信設定（プロファイル + ベクトル検索 + デザイン）でFindeeエージェントとの対話を実環境同様にテストできます。
        ステータスが「下書き」「停止中」でもテスト可能（公開不要）。
        {/* 2026-10-03 追記：AIプロファイルのプレビューとの違いを明記（SMART_HUB_DEV-98） */}
        <br />
        公開中のウィジェットと同じく、<strong>NGワード・禁止トピック</strong>、ナレッジ（固定回答）、商品データの検索も適用されます。
      </div>

      {/* 統計 */}
      {stats.turnCount > 0 && (
        <div className='bg-surface-secondary text-secondary flex gap-4 rounded-lg p-2.5 text-[11px]'>
          <span>会話ターン: <strong className='text-primary'>{stats.turnCount}</strong></span>
          <span>累計トークン: <strong className='text-primary'>{stats.totalTokens.toLocaleString()}</strong></span>
          <span>累計コスト: <strong className='text-success'>¥{stats.totalCost.toFixed(2)}</strong></span>
        </div>
      )}

      {error && (
        <div className='bg-surface-critical text-critical rounded-lg p-2.5 text-xs'>
          {error}
        </div>
      )}

      {/* 会話履歴 */}
      <div className='bg-surface-secondary flex max-h-[480px] min-h-[240px] flex-col gap-2.5 overflow-y-auto rounded-lg border border-secondary p-3'>
        {history.length === 0 ? (
          <div className='text-tertiary py-8 text-center text-xs'>
            メッセージを入力して送信ボタンを押してください
          </div>
        ) : history.map((m, i) => {
          const roleCls = m.role === 'user'
            ? 'bg-surface-emphasis border-emphasis ml-10'
            : m.role === 'system'
              ? 'bg-surface-critical border-critical-secondary'
              : 'bg-surface-success border-success mr-10'
          return (
            <div key={i} className={`rounded-lg border-l-4 p-2.5 ${roleCls}`}>
              <div className='text-secondary mb-1 text-[9px] font-semibold'>
                {m.role === 'user' ? '👤 あなた' : m.role === 'system' ? '⚠️ システム' : '🤖 AI'}
                {m.blocked && <span className='text-critical ml-1.5'>NGによりブロック</span>}
              </div>
              <div className='text-primary whitespace-pre-wrap text-[13px] leading-relaxed'>{m.content}</div>

              {/* 推薦商品 */}
              {m.recommendations && m.recommendations.length > 0 && (
                <div className='mt-2 flex flex-col gap-1'>
                  <div className='text-secondary text-[10px] font-semibold'>📦 推薦商品 ({m.recommendations.length}件):</div>
                  {m.recommendations.map((r, ri) => (
                    <div key={ri} className='bg-surface rounded p-1.5 text-[11px]'>
                      <strong className='text-primary'>{r.name || `商品 #${r.entity_id}`}</strong>
                      {r.category && <span className='text-secondary ml-1.5'>{r.category}</span>}
                      {r.price && <span className='text-success ml-1.5'>¥{r.price.toLocaleString()}</span>}
                      <span className='text-tertiary ml-2 text-[9px]'>類似度 {Math.round(r.similarity * 100)}%</span>
                    </div>
                  ))}
                </div>
              )}

              {/* メタ情報 */}
              {m.meta && (
                <div className='bg-surface text-secondary mt-1.5 flex flex-wrap gap-2 rounded p-1.5 text-[9px]'>
                  {m.meta.model && <span>モデル: <code className='text-emphasis'>{m.meta.model}</code></span>}
                  {m.meta.complexity_score !== undefined && <span>複雑度: <strong>{m.meta.complexity_score}</strong> ({m.meta.complexity_band})</span>}
                  {m.meta.latency_ms !== undefined && <span>応答時間: <strong>{m.meta.latency_ms}ms</strong></span>}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* 入力欄 */}
      <div className='flex gap-1.5'>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          // 送信は「送信」ボタンのみ。Enter に割り当てない。
          // 日本語入力の「スペースで変換 → Enter で確定」の確定 Enter も keydown で
          // 飛んでくるため変換した瞬間に誤送信され、また Enter は習慣で押してしまう
          placeholder="質問を入力（送信ボタンで送信）..."
          disabled={loading}
          className={`${INPUT} flex-1`}
        />
        <Button color={ButtonColor.PRIMARY} onClick={send} disabled={loading || !input.trim()}>{loading ? '生成中...' : '送信'}</Button>
        <Button color={ButtonColor.SECONDARY} onClick={reset} disabled={history.length === 0}>🔄 リセット</Button>
      </div>
    </div>
  )
}


// =====================
// 新規作成オーバーレイ
// =====================
function CreateCampaignOverlay({ siteId, authHeaders, aiProfiles, onClose, onCreated }) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [aiProfileId, setAiProfileId] = useState(aiProfiles[0]?.id || '')
  const [saving, setSaving] = useState(false)

  const submit = async () => {
    if (!name.trim()) {
      alert('配信名を入力してください')
      return
    }
    setSaving(true)
    try {
      const r = await fetch(`/api/sites/${siteId}/purchase-assist/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          name: name.trim(),
          description,
          ai_profile_id: aiProfileId ? parseInt(aiProfileId) : null,
        }),
      })
      if (!r.ok) {
        const d = await r.json().catch(() => ({}))
        throw new Error(d.detail || r.status)
      }
      const d = await r.json()
      onCreated(d)
    } catch (e) {
      alert(`作成失敗: ${e.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Overlay onClose={onClose} maxWidth={520}>
      <div className='p-4'>
        <h3 className='text-emphasis m-0 mb-3 text-sm font-bold'>+ 新規配信作成</h3>
        <p className='text-secondary m-0 mb-3.5 text-[11px]'>
          配信名と基本情報を入力してください。詳細設定（対象ページ・起動条件・デザイン等）は作成後に編集できます。
        </p>
        <div className='flex flex-col gap-3'>
          <Field label="配信名" required>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="例: 商品詳細ページ向け配信" className={INPUT} />
          </Field>
          <Field label="説明（任意）">
            <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} className={TEXTAREA} />
          </Field>
          <Field label="AIプロファイル" hint="後で変更可能">
            <select value={aiProfileId} onChange={e => setAiProfileId(e.target.value)} className={INPUT}>
              <option value="">— 未選択 —</option>
              {aiProfiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
        </div>
        <div className='mt-4 flex justify-end gap-1.5'>
          <Button color={ButtonColor.SECONDARY} onClick={onClose}>取消</Button>
          <Button color={ButtonColor.PRIMARY} onClick={submit} disabled={saving}>{saving ? '作成中...' : '+ 作成'}</Button>
        </div>
      </div>
    </Overlay>
  )
}


// =====================
// 共通コンポーネント
// =====================
function Overlay({ children, onClose, maxWidth = 720 }) {
  return (
    <ModalOverlay onClose={onClose} className='fixed inset-0 z-modal flex items-center justify-center bg-black/60 p-6'>
      <div onClick={e => e.stopPropagation()} className={`${CARD} flex max-h-[90vh] w-full flex-col overflow-auto`} style={{ maxWidth }}>
        {children}
      </div>
    </ModalOverlay>
  )
}

function Field({ label, hint, children, required }) {
  return (
    <div className={FIELD}>
      <label className={LABEL}>
        {label}{required && <span className='text-critical ml-0.5'>*</span>}
        {hint && <span className='text-tertiary ml-1.5 text-[10px] font-normal'>— {hint}</span>}
      </label>
      {children}
    </div>
  )
}

function SectionTitle({ children }) {
  return (
    <h4 className='text-emphasis m-0 mt-1.5 text-xs font-bold'>{children}</h4>
  )
}

/**
 * 折りたためるセクション。
 * 一度に全部を展開すると縦に長くなり、いま何を設定しているのか見失うため、
 * 必要なものだけ開いて使う。`defaultOpen` で初期状態を決める。
 */
function Section({ title, note, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className='rounded-lg border border-secondary'>
      <button
        type='button' onClick={() => setOpen(v => !v)}
        className='hover:bg-surface-hover flex w-full cursor-pointer items-center gap-1.5 rounded-lg px-2.5 py-2 text-left'
      >
        <span className='text-tertiary text-[10px]'>{open ? '▼' : '▶'}</span>
        <span className='text-emphasis text-xs font-bold'>{title}</span>
        {note && <span className='text-tertiary ml-1 text-[10px] font-normal'>{note}</span>}
      </button>
      {open && <div className='flex flex-col gap-3.5 border-t border-secondary p-2.5'>{children}</div>}
    </div>
  )
}

function CheckRow({ label, checked, onChange }) {
  return (
    <label className='text-primary flex cursor-pointer items-center gap-1.5 text-xs'>
      <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  )
}
